import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { getStoredTheme } from "./theme";
import "./styles/global.css";
import "./styles/ds.css";
import { ConfirmProvider, ToastProvider } from "./ds";

// Applied before the first render so there's no flash of the wrong theme
// on load for anyone who previously chose dark mode.
document.documentElement.setAttribute("data-theme", getStoredTheme());

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

// Local UI preview mode: intercepts all API calls with realistic fake data
// via MSW, so the platform can be looked at without a backend/database.
// Defaults on in dev; set VITE_USE_MOCKS=false to talk to a real backend.
const useMocks = import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS !== "false" && import.meta.env.VITE_REVIEW !== "1";

async function enableMocking() {
  if (!useMocks) return;
  const { worker } = await import("./mocks/browser");
  await worker.start({ onUnhandledRequest: "bypass" });
}

enableMocking().then(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <ConfirmProvider>
                  <App />
                </ConfirmProvider>
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
});
